import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
} from "reactstrap";
import { Btn } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile, mediaUrl } from "../../Utils"; // import apiGet for branches
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAuth } from "../../Context/AuthContext";

interface FormValues {
    name: string;
    email: string;
    phone_number: string;
    status: string;
    gender: string;
    date_of_birth: string;
    address: string;
    blood_group: string;
    nationality: string;
    license_number: string;
    license_expiry_date: string;
    joining_date: string;
    years_of_experience: string;
    assigned_branch_id: string; // Keep as string (ID from select)
    driver_image: FileList;
    license_image_front: FileList;
    license_image_back: FileList;
}

interface BranchOption {
    id: number;
    branch_name: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function EditDriver() {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [branches, setBranches] = useState<BranchOption[]>([]);
    const { hasPermission } = useAuth();
    const {
        handleSubmit,
        control,
        setValue, reset,
        formState: { errors },
    } = useForm<FormValues>();

    const [previews, setPreviews] = useState({
        driver: null as string | null,
        licenseFront: null as string | null,
        licenseBack: null as string | null
    });

    const validatePhoneNumber = (value: string) => {
        if (!value) return "Phone number is required.";
        const sanitizedValue = value.replace(/\D/g, "");
        const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);
        if (!phoneNumber || !phoneNumber.isValid()) return "Invalid phone number.";
        const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
        if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
            return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
        }
        return true;
    };

    useEffect(() => {
        const fetchDriverDetails = async () => {
            try {
                if (!location.state || !location.state.id) {
                    toast.error("No Driver ID found");
                    navigate(`${process.env.PUBLIC_URL}/driver`);
                    return;
                }

                // Fetch branches first or in parallel
                try {
                    const branchData: any = await apiPost("branches/list", { status: 'Active' });
                    setBranches(branchData);
                } catch (e) {
                    console.error("Failed to fetch branches", e);
                }

                const result = await apiPost("drivers/viewbyid", { id: location.state.id });
                setValue("name", result.name);
                setValue("email", result.email);
                setValue("phone_number", result.phone_number);
                setValue("status", result.status);
                setValue("gender", result.gender);
                setValue("date_of_birth", result.date_of_birth);
                setValue("address", result.address);
                setValue("blood_group", result.blood_group);
                setValue("nationality", result.nationality);
                setValue("license_number", result.license_number);
                setValue("license_expiry_date", result.license_expiry_date);
                setValue("joining_date", result.joining_date);
                setValue("years_of_experience", result.years_of_experience);
                setValue("assigned_branch_id", result.assigned_branch_id); // Set logic might need type conversion if result is number so string expected

                setPreviews({
                    driver: result.driver_image ? `${mediaUrl}${result.driver_image}` : null,
                    licenseFront: result.license_image_front ? `${mediaUrl}${result.license_image_front}` : null,
                    licenseBack: result.license_image_back ? `${mediaUrl}${result.license_image_back}` : null
                });

                setLoading(false);
            } catch (error) {
                console.error("Error fetching driver details:", error);
                toast.error("Failed to fetch driver details");
                setLoading(false);
            }
        };
        fetchDriverDetails();
    }, [setValue, location.state, navigate]);


    const onSubmit = async (data: FormValues) => {
        setIsButtonDisabled(true);
        try {
            let driverImageName = "";
            let licenseFrontName = "";
            let licenseBackName = "";

            if (data.driver_image && data.driver_image[0] instanceof File) {
                driverImageName = await uploadFile(data.driver_image[0]);
            }
            if (data.license_image_front && data.license_image_front[0] instanceof File) {
                licenseFrontName = await uploadFile(data.license_image_front[0]);
            }
            if (data.license_image_back && data.license_image_back[0] instanceof File) {
                licenseBackName = await uploadFile(data.license_image_back[0]);
            }

            const formData = new FormData();
            formData.append("id", location.state.id);
            formData.append("name", data.name);
            formData.append("email", data.email);
            formData.append("phone_number", data.phone_number);
            formData.append("status", data.status);
            formData.append("gender", data.gender);
            formData.append("date_of_birth", data.date_of_birth);
            if (data.blood_group) formData.append("blood_group", data.blood_group);
            formData.append("address", data.address);
            formData.append("nationality", data.nationality);
            formData.append("license_number", data.license_number);
            formData.append("license_expiry_date", data.license_expiry_date);
            formData.append("joining_date", data.joining_date);
            formData.append("years_of_experience", data.years_of_experience);
            formData.append("assigned_branch_id", data.assigned_branch_id);

            if (driverImageName) formData.append("driver_image", driverImageName);
            if (licenseFrontName) formData.append("license_image_front", licenseFrontName);
            if (licenseBackName) formData.append("license_image_back", licenseBackName);

            await apiPost("drivers/update", formData);

            toast.success("Driver updated successfully", { autoClose: 1000 });
            reset();
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/driver`);
            }, 1000);
        } catch (err: any) {
            if (err.response && err.response.data && err.response.data.errors) {
                Object.values(err.response.data.errors).forEach((error: any) => {
                    toast.error(error[0] || "Validation Error", { autoClose: 3000 });
                });
            } else {
                const errorMessage = err.response?.data?.message || "An unexpected error occurred";
                toast.error(errorMessage, { autoClose: 1000 });
            }
        } finally {
            setIsButtonDisabled(false);
        }
    };

    const handleImageChange = (files: FileList | null, type: 'driver' | 'licenseFront' | 'licenseBack', fieldOnChange: (files: FileList) => void) => {
        if (files && files[0]) {
            if (files[0].size > 2 * 1024 * 1024) {
                toast.error("File size must be less than 2MB");
                const fileInputId = 'input_' + type;
                // We don't have direct access to input ref here, but we can prevent value update.
                // However, to clear the input, it's harder without passing the event target. 
                // Since we pass e.target.files to this function, we can't easily clear e.target.
                // Let's rely on not calling fieldOnChange and showing error.
                return;
            }
            fieldOnChange(files);
            setPreviews(prev => ({ ...prev, [type]: URL.createObjectURL(files[0]) }));
        }
    }

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="Edit Driver" parent="General" title="Edit Driver" />
            {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '70vh' }}><Spinner color="primary" /></div> : (
                <Container fluid>
                    <Col xl={12} className="mb-lg-3">
                        <Card>
                            <CardBody className="custom-input form-validation">
                                <Form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
                                    <Row className="g-3">
                                        {/* Render previous fields */}
                                        <Col md={4}>
                                            <Label>Name <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="name"
                                                control={control}
                                                rules={{ required: "Name is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Enter Name" invalid={!!errors.name} style={{ borderColor: errors.name ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.name?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>Email <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="email"
                                                control={control}
                                                rules={{ required: "Email is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="email" {...field} placeholder="Enter Email" invalid={!!errors.email} style={{ borderColor: errors.email ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.email?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Phone Number <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="phone_number"
                                                control={control}
                                                rules={{ required: "Phone number is required", validate: validatePhoneNumber }}
                                                render={({ field }) => (
                                                    <>
                                                        <PhoneInput
                                                            country={"ae"}
                                                            onlyCountries={["ae", "sa", "in"]}
                                                            preferredCountries={["ae"]}
                                                            value={field.value}
                                                            onChange={(value) => field.onChange(value)}
                                                            inputProps={{
                                                                name: "phone_number",
                                                                placeholder: "Enter Phone",
                                                                autoComplete: "none"
                                                            }}
                                                            inputStyle={{
                                                                width: '100%',
                                                                borderColor: errors.phone_number ? 'red' : '#ced4da'
                                                            }}
                                                        />
                                                        {errors.phone_number && <FormFeedback style={{ display: 'block' }}>{errors.phone_number.message}</FormFeedback>}
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>Status <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="status"
                                                control={control}
                                                rules={{ required: "Status is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.status} style={{ borderColor: errors.status ? "red" : "" }} autoComplete="off">
                                                            <option value="">Select Status</option>
                                                            <option value="Active">Active</option>
                                                            <option value="Inactive">Inactive</option>
                                                        </Input>
                                                        <FormFeedback>{errors.status?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Gender <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="gender"
                                                control={control}
                                                rules={{ required: "Gender is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.gender} style={{ borderColor: errors.gender ? "red" : "" }} autoComplete="off">
                                                            <option value="">Select Gender</option>
                                                            <option value="Male">Male</option>
                                                            <option value="Female">Female</option>
                                                            <option value="Other">Other</option>
                                                        </Input>
                                                        <FormFeedback>{errors.gender?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Date of Birth <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="date_of_birth"
                                                control={control}
                                                rules={{ required: "DOB is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            className={`form-control ${errors.date_of_birth ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                            placeholderText="Select Date of Birth"
                                                            dateFormat="yyyy-MM-dd"
                                                            maxDate={new Date()}
                                                            peekNextMonth
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                            autoComplete="off"
                                                        />
                                                        <FormFeedback style={{ display: errors.date_of_birth ? "block" : "none" }}>{errors.date_of_birth?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Blood Group <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="blood_group"
                                                control={control}
                                                rules={{ required: "Blood Group is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.blood_group} style={{ borderColor: errors.blood_group ? "red" : "" }} autoComplete="off">
                                                            <option value="">Select Blood Group</option>
                                                            <option value="A+">A+</option>
                                                            <option value="A-">A-</option>
                                                            <option value="B+">B+</option>
                                                            <option value="B-">B-</option>
                                                            <option value="O+">O+</option>
                                                            <option value="O-">O-</option>
                                                            <option value="AB+">AB+</option>
                                                            <option value="AB-">AB-</option>
                                                        </Input>
                                                        <FormFeedback>{errors.blood_group?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={8}>
                                            <Label>Address <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="address"
                                                control={control}
                                                rules={{ required: "Address is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="textarea" rows="1" {...field} autoComplete="off" placeholder="Enter Address" invalid={!!errors.address} style={{ borderColor: errors.address ? "red" : "" }} />
                                                        <FormFeedback>{errors.address?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Nationality <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="nationality"
                                                control={control}
                                                rules={{ required: "Nationality is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Enter Nationality" invalid={!!errors.nationality} style={{ borderColor: errors.nationality ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.nationality?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>License Number <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="license_number"
                                                control={control}
                                                rules={{ required: "License Number is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input {...field} placeholder="Enter License No" invalid={!!errors.license_number} style={{ borderColor: errors.license_number ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.license_number?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>License Expiry Date <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="license_expiry_date"
                                                control={control}
                                                rules={{ required: "Expiry Date is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            className={`form-control ${errors.license_expiry_date ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                            placeholderText="Select Expiry Date"
                                                            dateFormat="yyyy-MM-dd"
                                                            // minDate={new Date()}
                                                            peekNextMonth
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                            autoComplete="off"
                                                        />
                                                        <FormFeedback style={{ display: errors.license_expiry_date ? "block" : "none" }}>{errors.license_expiry_date?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Label>Joining Date <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="joining_date"
                                                control={control}
                                                rules={{ required: "Joining Date is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <DatePicker
                                                            className={`form-control ${errors.joining_date ? "is-invalid" : ""}`}
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? formatDate(date) : "")}
                                                            placeholderText="Select Joining Date"
                                                            dateFormat="yyyy-MM-dd"
                                                            maxDate={new Date()}
                                                            peekNextMonth
                                                            showMonthDropdown
                                                            showYearDropdown
                                                            dropdownMode="select"
                                                            autoComplete="off"
                                                        />
                                                        <FormFeedback style={{ display: errors.joining_date ? "block" : "none" }}>{errors.joining_date?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>Years of Experience <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="years_of_experience"
                                                control={control}
                                                rules={{ required: "Experience is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="number" {...field} placeholder="Enter Experience (Years)" invalid={!!errors.years_of_experience} style={{ borderColor: errors.years_of_experience ? "red" : "" }} autoComplete="off" />
                                                        <FormFeedback>{errors.years_of_experience?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>Assigned Branch <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="assigned_branch_id"
                                                control={control}
                                                defaultValue=""
                                                rules={{ required: "Branch is required" }}
                                                render={({ field }) => (
                                                    <>
                                                        <Input type="select" {...field} invalid={!!errors.assigned_branch_id} style={{ borderColor: errors.assigned_branch_id ? "red" : "" }} autoComplete="off">
                                                            <option value="">Select Branch</option>
                                                            {branches.map(branch => (
                                                                <option key={branch.id} value={branch.id}>{branch.branch_name}</option>
                                                            ))}
                                                        </Input>
                                                        <FormFeedback>{errors.assigned_branch_id?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        {/* Removed Vehicle ID */}

                                        {/* Image Fields with Preview */}
                                        <Col md={4}>
                                            <Label>Driver Image</Label>
                                            <Controller
                                                name="driver_image"
                                                control={control}
                                                render={({ field: { value, onChange, ...field } }) => (
                                                    <>
                                                        <Input type="file" {...field} onChange={(e) => handleImageChange(e.target.files, 'driver', onChange)} accept="image/*" autoComplete="off" />
                                                        {previews.driver && !(value instanceof FileList && value.length > 0) && (
                                                            <a href={previews.driver} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                        )}
                                                        {value instanceof FileList && value.length > 0 && (
                                                            <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>License Front Image <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="license_image_front"
                                                control={control}
                                                rules={{ required: !previews.licenseFront && "Front Image is required" }}
                                                render={({ field: { value, onChange, ...field } }) => (
                                                    <>
                                                        <Input type="file" {...field} onChange={(e) => handleImageChange(e.target.files, 'licenseFront', onChange)} accept="image/*" autoComplete="off" invalid={!!errors.license_image_front} style={{ borderColor: errors.license_image_front ? "red" : "" }} />
                                                        {previews.licenseFront && !(value instanceof FileList && value.length > 0) && (
                                                            <a href={previews.licenseFront} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                        )}
                                                        {value instanceof FileList && value.length > 0 && (
                                                            <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                        )}
                                                        <FormFeedback style={{ display: errors.license_image_front ? 'block' : 'none' }}>{errors.license_image_front?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Label>License Back Image <span className="text-danger">*</span></Label>
                                            <Controller
                                                name="license_image_back"
                                                control={control}
                                                rules={{ required: !previews.licenseBack && "Back Image is required" }}
                                                render={({ field: { value, onChange, ...field } }) => (
                                                    <>
                                                        <Input type="file" {...field} onChange={(e) => handleImageChange(e.target.files, 'licenseBack', onChange)} accept="image/*" autoComplete="off" invalid={!!errors.license_image_back} style={{ borderColor: errors.license_image_back ? "red" : "" }} />
                                                        {previews.licenseBack && !(value instanceof FileList && value.length > 0) && (
                                                            <a href={previews.licenseBack} target="_blank" rel="noreferrer" className="d-block mt-2">View Current Document</a>
                                                        )}
                                                        {value instanceof FileList && value.length > 0 && (
                                                            <a href={URL.createObjectURL(value[0])} target="_blank" rel="noreferrer" className="d-block mt-2">View New Document</a>
                                                        )}
                                                        <FormFeedback style={{ display: errors.license_image_back ? 'block' : 'none' }}>{errors.license_image_back?.message}</FormFeedback>
                                                    </>
                                                )}
                                            />
                                        </Col>
                                        <Col xs={12} className="mt-2">
                                            <Btn color="primary" disabled={isButtonDisabled}>
                                                {isButtonDisabled ? <>Updating <Spinner size="sm" /></> : "Update"}

                                            </Btn>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Container>
            )}
            <ToastContainer autoClose={1000} />
        </div>
    );
}
